/*
 * Keeps the stage current after first paint.
 *
 * The inline resolver in the section already stamped the right stage on <html>
 * before anything rendered. This file only handles what happens afterwards:
 * the visitor submitting, and the wait elapsing while the tab stays open.
 */
(function () {
  'use strict';

  var PHASES = ['before_submit', 'after_submit', 'after_wait', 'after_retest'];
  var BANDS = ['depleted', 'low', 'threshold', 'adequate', 'good', 'optimal'];
  var DEFAULT_WAIT = 5400;

  function params() {
    try {
      return new URLSearchParams(window.location.search);
    } catch (_) {
      return null;
    }
  }

  function param(name) {
    var p = params();
    return p ? p.get(name) : null;
  }

  function stored() {
    try {
      return JSON.parse(localStorage.getItem('bl_event_quiz')) || {};
    } catch (_) {
      return {};
    }
  }

  function config() {
    return document.querySelector('[data-bl-wait]');
  }

  function waitSeconds() {
    var configured = DEFAULT_WAIT;
    var node = config();
    if (node) {
      var parsed = parseInt(node.dataset.blWait, 10);
      if (parsed > 0) configured = parsed;
    }
    var override = parseInt(param('bl_test_delay'), 10);
    if (override > 0 && override <= configured) return override;
    return configured;
  }

  function ttlSeconds() {
    var node = config();
    if (!node) return 0;
    var parsed = parseInt(node.dataset.blTtl, 10);
    return parsed > 0 ? parsed : 0;
  }

  /* Same rules as the inline resolver. Derived every time rather than stored,
     so it cannot drift from the data it describes. */
  function resolve() {
    // The editor's forced stage counts too, otherwise this would immediately
    // undo what the inline resolver set.
    var node = config();
    var forced = param('bl_phase') || (node ? node.dataset.blForce : null);
    if (PHASES.indexOf(forced) !== -1) return forced;

    var state = stored();
    var ttl = ttlSeconds();
    // A booth device is shared; an expired session starts clean.
    if (ttl > 0 && state.reading1At && Date.now() / 1000 - state.reading1At > ttl) state = {};
    var urlBand = BANDS.indexOf(param('r1')) === -1 ? null : param('r1');
    var urlAt = parseInt(param('t'), 10);
    if (!(urlAt > 0)) urlAt = null;

    var submitted = Boolean(state.email) || Boolean(urlBand && urlAt);
    var readingAt = state.reading1At || urlAt;
    var retested = Boolean(state.reading2) || BANDS.indexOf(param('r2')) !== -1;

    if (retested) return 'after_retest';
    if (!submitted) return 'before_submit';
    if (readingAt && Date.now() / 1000 >= readingAt + waitSeconds()) return 'after_wait';
    return 'after_submit';
  }

  var timer = null;
  var swapTimer = null;
  var transitioning = false;
  var settled = false;

  function curtainMs() {
    var node = config();
    if (!node) return 0;
    var parsed = parseInt(node.dataset.blCurtainMs, 10);
    return parsed > 0 ? parsed : 0;
  }

  /* Puts the stage on <html> and tells everyone listening. Everything that
     should happen out of sight belongs here, not around the call. */
  function commit(phase) {
    var root = document.documentElement;
    if (root.getAttribute('data-bl-phase') !== phase) {
      root.setAttribute('data-bl-phase', phase);
      document.dispatchEvent(new CustomEvent('bl:event-phase', { detail: { phase: phase }, bubbles: true }));
    }
    schedule(phase);
  }

  function raise(phase, ms) {
    var el = document.querySelector('[data-bl-curtain]');
    if (!el) {
      commit(phase);
      return;
    }

    var text = el.querySelector('[data-bl-curtain-text]');
    var copy = el.getAttribute('data-bl-copy-' + phase.replace(/_/g, '-'));
    // An empty message leaves the spinner alone rather than an empty line.
    if (text) text.textContent = copy || '';

    transitioning = true;
    document.documentElement.setAttribute('data-bl-transition', phase);
    swapTimer = setTimeout(lower, ms);
  }

  function lower() {
    if (swapTimer) {
      clearTimeout(swapTimer);
      swapTimer = null;
    }
    transitioning = false;

    // Resolved again rather than reused: the wait can elapse while the curtain
    // is up, and the visitor should land on the stage that is true now.
    commit(resolve());
    // The previous stage may have been scrolled well down the page. Reset
    // before the curtain lifts so the new stage starts where it was designed to.
    try { window.scrollTo(0, 0); } catch (_) {}

    document.documentElement.removeAttribute('data-bl-transition');
  }

  function apply() {
    // The inline resolver already stamped the stage before paint. That first
    // agreement is the starting state, not a transition, and must never raise
    // the curtain, so the flag is cleared on the first pass whichever way it goes.
    var initial = !settled;
    settled = true;

    var next = resolve();
    var current = document.documentElement.getAttribute('data-bl-phase');
    if (current === next) {
      if (!transitioning) schedule(next);
      return;
    }

    var ms = curtainMs();
    // A curtain nobody is looking at only delays the swap. A backgrounded tab
    // also throttles the timer, which would strand it raised.
    if (initial || transitioning || ms <= 0 || document.hidden) {
      commit(next);
      return;
    }

    raise(next, ms);
  }

  function autoAdvance() {
    var node = config();
    return Boolean(node) && node.dataset.blAutoAdvance === 'true';
  }

  /* Off by default: swapping the screen out from under someone still reading
     their first result reads as a glitch, not as progress. The stage still
     moves the next time the page is opened or brought back to the front, which
     covers both the SMS link and a visitor returning to the tab. */
  function schedule(phase) {
    if (timer) clearTimeout(timer);
    if (!autoAdvance()) return;
    if (phase !== 'after_submit') return;

    var state = stored();
    if (!state.reading1At) return;

    var remaining = (state.reading1At + waitSeconds()) - Date.now() / 1000;
    if (remaining <= 0) return;
    timer = setTimeout(apply, Math.ceil(remaining * 1000));
  }

  document.addEventListener('bl:event-quiz:submitted', apply);
  document.addEventListener('bl:event-retest:submitted', apply);

  // A phone that was asleep through the boundary needs a nudge on return.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    // Background timers are throttled, so a curtain raised on the way out can
    // still be up on the way back. Settle it instead of leaving it hanging.
    if (transitioning) lower();
    apply();
  });

  apply();
})();
